import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SlidePanel, DayTimeline, ResourceChip, RouteMapMini } from "./panelChrome";
import { Plus, Loader2, Trash2, Clock, ShieldCheck } from "lucide-react";

const ordinal = (n) => { const s = ["th", "st", "nd", "rd"], v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); };
const fmtDay = (iso) => { const d = new Date(iso + "T00:00:00"); return `${ordinal(d.getDate())} ${d.toLocaleString("en-GB", { month: "long" })} ${d.getFullYear()}`; };

const SECTIONS = [
  { key: "user_unavailabilities", label: "User Unavailabilities", kind: "user_unavailability", scope: "user", empty: "No Unavailabilities Found" },
  { key: "holidays", label: "Holidays", kind: null, empty: "No Holidays Found" },
  { key: "absences", label: "Absences", kind: null, empty: "No Absences Found" },
  { key: "asset_unavailabilities", label: "Asset Unavailabilities", kind: "asset_unavailability", scope: "asset", empty: "No Unavailabilities Found" },
  { key: "asset_maintenances", label: "Asset Maintenances", kind: "asset_maintenance", scope: "asset", empty: "No Asset Maintenances Found" },
  { key: "asset_qualifications", label: "Asset Qualifications", kind: "asset_qualification", scope: "asset", empty: "No Asset Qualifications Found" },
  { key: "user_qualifications", label: "User Qualifications", kind: "user_qualification", scope: "user", empty: "No Results Found" },
];

export default function DaySummaryPanel({ vehicle, day, onClose, onReload }) {
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(null); // section key
  const [label, setLabel] = useState("");
  const [detail, setDetail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/scheduler/day-summary", { params: { vehicle, day } }).then((r) => setData(r.data)).catch(() => toast.error("Could not load day"));
  }, [vehicle, day]);
  useEffect(() => { load(); }, [load]);

  const canWrite = data?.can_write;
  const openAdd = (key) => { setAdding(key); setLabel(""); setDetail(""); };
  const submit = async (sec) => {
    if (!label.trim()) { toast.error("A label is required"); return; }
    setBusy(true);
    try {
      await api.post("/scheduler/day-events", { kind: sec.kind, date: day, label: label.trim(), detail: detail.trim(), vehicle_legacy_id: sec.scope === "asset" ? vehicle : null });
      toast.success("Added"); setAdding(null); load(); onReload?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
    finally { setBusy(false); }
  };
  const del = async (id) => { if (!window.confirm("Remove this entry?")) return; try { await api.delete(`/scheduler/day-events/${id}`); toast.success("Removed"); load(); onReload?.(); } catch (e) { toast.error("Could not remove"); } };

  return (
    <SlidePanel onClose={onClose} maxW="max-w-6xl" testId="day-summary-panel">
      {!data ? <div className="flex-1 flex items-center justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
        <>
          <div className="flex items-center justify-between gap-2 border-b border-slate-100 pl-5 pr-14 py-4">
            <h3 className="text-xl font-bold text-slate-900">{fmtDay(day)} - Summary</h3>
            <div className="flex items-center gap-1.5">
              <Button variant="outline" size="sm" onClick={() => toast.info("Demonstration action")} data-testid="return-work-bank">Return to Job Pool</Button>
              <Button variant="outline" size="sm" onClick={() => toast.info("Demonstration action")} data-testid="reassign-day">Reassign Day</Button>
            </div>
          </div>
          <div className="flex flex-1 flex-col sm:flex-row overflow-hidden">
            <div className="w-full sm:w-96 shrink-0 border-b sm:border-b-0 sm:border-r border-slate-100 bg-slate-50/60 flex flex-col min-h-0">
              <RouteMapMini fill taskId={data.map_task_legacy_id} />
              <div className="shrink-0 max-h-72 overflow-y-auto border-t border-slate-100"><DayTimeline embedded steps={data.day_timeline} vehicleTitle={data.vehicle.title} onOpenDay={() => {}} /></div>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              <dl className="space-y-2.5 text-sm mb-5">
                <div className="grid grid-cols-[140px_1fr] gap-3"><dt className="text-slate-400">Started At</dt><dd className="text-slate-800"><Clock className="inline w-4 h-4 text-slate-400 mr-1" />{data.started_at || "—"}</dd></div>
                <div className="grid grid-cols-[140px_1fr] gap-3"><dt className="text-slate-400">Finished At</dt><dd className="text-slate-800"><Clock className="inline w-4 h-4 text-slate-400 mr-1" />{data.finished_at || "—"}</dd></div>
                <div className="grid grid-cols-[140px_1fr] gap-3"><dt className="text-slate-400">Vehicle Inspections</dt><dd><span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700"><ShieldCheck className="inline w-3.5 h-3.5 mr-1" />{data.vehicle_inspections.done}/{data.vehicle_inspections.total || 0}</span></dd></div>
                <div className="grid grid-cols-[140px_1fr] gap-3"><dt className="text-slate-400">Resources</dt><dd className="flex flex-wrap gap-1.5">{data.resources.map((r, i) => <ResourceChip key={i} name={r.name} kind={r.kind} job_title={r.job_title} />)}</dd></div>
              </dl>

              <div className="space-y-3">
                {SECTIONS.map((sec) => {
                  const rows = data.sections[sec.key] || [];
                  return (
                    <div key={sec.key} data-testid={`day-section-${sec.key}`}>
                      <div className="flex items-center justify-between">
                        <h5 className="text-base font-semibold text-slate-800">{sec.label}</h5>
                        {canWrite && sec.kind && <button onClick={() => openAdd(sec.key)} className="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50" data-testid={`day-add-${sec.key}`}><Plus className="w-4 h-4" /></button>}
                      </div>
                      {adding === sec.key && (
                        <div className="mt-2 rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 space-y-2" data-testid={`day-addform-${sec.key}`}>
                          <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={sec.scope === "user" ? "Staff member / reason" : "Asset detail"} data-testid={`day-addlabel-${sec.key}`} />
                          <Input value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="Detail (optional)" />
                          <div className="flex justify-end gap-2"><Button variant="outline" size="sm" onClick={() => setAdding(null)}>Cancel</Button><Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy} onClick={() => submit(sec)} data-testid={`day-addsave-${sec.key}`}>{busy && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}Add</Button></div>
                        </div>
                      )}
                      <div className="mt-2 rounded-lg border border-slate-200">
                        {rows.length === 0 ? <div className="py-3 text-center text-sm text-slate-400">{sec.empty}</div> : (
                          <ul className="divide-y divide-slate-100">
                            {rows.map((r, i) => (
                              <li key={r.id || i} className="flex items-center justify-between px-3 py-2 text-sm">
                                <div><div className="font-medium text-slate-700">{r.label}</div>{r.detail && <div className="text-xs text-slate-400">{r.detail}</div>}</div>
                                {canWrite && r.id && sec.kind && <button onClick={() => del(r.id)} className="text-slate-300 hover:text-rose-500" data-testid={`day-del-${r.id}`}><Trash2 className="w-4 h-4" /></button>}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </SlidePanel>
  );
}
